import { TranslationsContext } from '@teliaee/telia-translate';
import React from 'react';
import { Grid, GridColumn, Separator, Table, TGridColumnWidth } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import CmsDataStore from '../stores/CmsDataStore';
import { MetaInfo } from './packet/types';

interface ProductOverviewProps {
  productDataField?: string;
  packetDataField?: string;
  gridWidth?: TGridColumnWidth;
  showTitle?: boolean;
  present?: TableData;
  cmsDataStore?: CmsDataStore;
}

interface TableData {
  item: string;
  price: JSX.Element;
}

export interface PacketItem extends MetaInfo {
  title: string;
  pricePerMonth: {
    value: string;
  };
}

export interface ProductItem extends MetaInfo {
  name: string;
  brand: string;
  tag: {
    value: string;
    label: string;
  };
}

export interface MetaElement {
  item: string;
  price: JSX.Element;
}

@observer
class ProductOverview extends React.Component<ProductOverviewProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  private format = (num: number) => num.toFixed(2).replace('.', ',');
  private parse = (str: string) => (str ? parseFloat(str.replace(',', '.')) : 0);

  public static defaultProps: Partial<ProductOverviewProps> = {
    productDataField: 'selectedProduct',
    packetDataField: 'selectedPacket',
    gridWidth: 'xs-12',
    showTitle: true,
    present: null
  };

  private totalValue(productValue: string, packetValue: string) {
    const totalValue = this.parse(productValue) + this.parse(packetValue);
    return this.format(totalValue);
  }

  private baseTableData(productItem: ProductItem, packetItem: PacketItem, productValue: string, packetValue: string, label: string) {
    const translate = this.context.translate;
    const totalValue = this.totalValue(productValue, packetValue);
    return [
      {
        item: translate('productOverview.product', { brand: productItem.brand, name: productItem.name }),
        price: (
          <span>
            {productValue} {label}
          </span>
        )
      },
      {
        item: translate('productOverview.packet', { title: packetItem.title }),
        price: (
          <span>
            {packetValue} {label}
          </span>
        )
      },
      {
        item: <strong>{translate('productOverview.total')}</strong>,
        price: (
          <strong>
            {totalValue} {label}
          </strong>
        )
      }
    ] as TableData[];
  }

  private renderNothingChosen() {
    const translate = this.context.translate;
    const fallbackText = 'Täpse pakkumise saad teada teenindajalt. Esita tellimus ja Sinuga võetakse ühendust';
    return (
      <div className="text">
        {this.props.showTitle ? <h2 className="h3">{translate('productOverview.label')}</h2> : null}
        <p className="h4">{translate('productOverview.nothingChosen') || fallbackText}</p>
      </div>
    );
  }

  private renderTableWithMeta(productItem: ProductItem, packetItem: PacketItem) {
    const translate = this.context.translate;
    const { value: productValue, label } = productItem.meta.product;
    const { value: packetValue } = packetItem.pricePerMonth;
    const data = this.baseTableData(productItem, packetItem, productValue, packetValue, label);
    const metaData = [] as MetaElement[];
    const productDiscount = productItem.meta.discount;
    const packetDiscount = packetItem.meta && packetItem.meta.discount;
    if (productDiscount || packetDiscount) {
      const productDiscountValue = this.parse(productDiscount && productDiscount.value);
      const packetDiscountValue = this.parse(packetDiscount && packetDiscount.value);
      const discount = productDiscountValue + packetDiscountValue;
      const discountLabel = (productDiscount && productDiscount.label) || packetDiscount.label;
      metaData.push({
        item: translate('productOverview.discount'),
        price: (
          <strong>
            {this.format(discount)} {discountLabel}
          </strong>
        )
      });
    }

    if (productItem.meta.present || (packetItem.meta && packetItem.meta.present)) {
      const present = productItem.meta.present || (packetItem.meta && packetItem.meta.present);
      metaData.push({
        item: translate('productOverview.present', { name: present.name }),
        price: (
          <span>
            {this.format(this.parse(present.value))} {present.label}
          </span>
        )
      });
    }

    return this.renderTable(data, metaData);
  }

  private renderTableWithTag(productItem: ProductItem, packetItem: PacketItem) {
    const { value: productValue, label } = productItem.tag;
    const { value: packetValue } = packetItem.pricePerMonth;
    const data = this.baseTableData(productItem, packetItem, productValue, packetValue, label);

    if (this.props.present) {
      data.push(this.props.present);
    }

    return this.renderTable(data);
  }

  private renderTable(data: TableData[], metaData: MetaElement[] = []) {
    const translate = this.context.translate;
    return (
      <div className="text">
        {this.props.showTitle ? <h2 className="h3">{translate('productOverview.label')}</h2> : null}
        <Grid align={['center-xs']}>
          <GridColumn width={[this.props.gridWidth]}>
            <Table hover={true}>
              <tbody>
                {data.map((row, idx) => {
                  return (
                    <tr key={`${1}row${idx}`}>
                      <td key={`${1}item${idx}`}>{row.item}</td>
                      <td className="text-right" key={`${1}price${idx}`}>
                        {row.price}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {metaData.length > 0 && <Separator spacing={'small'} color={'invisible'} />}
            {metaData.length > 0 && (
              <Table hover={true}>
                <tbody>
                  {metaData.map((row, idx) => {
                    return (
                      <tr key={`${2}row${idx}`}>
                        <td key={`${2}item${idx}`}>{row.item}</td>
                        <td className="text-right" key={`${2}price${idx}`}>
                          {row.price}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </GridColumn>
        </Grid>
      </div>
    );
  }

  public render() {
    const { cmsDataStore, productDataField, packetDataField } = this.props;
    const productItem = cmsDataStore.getData(productDataField);
    const packetItem = cmsDataStore.getData(packetDataField);
    if (productItem && productItem.meta && packetItem && packetItem.pricePerMonth) {
      return this.renderTableWithMeta(productItem, packetItem);
    }
    if (productItem && productItem.tag && packetItem && packetItem.pricePerMonth) {
      return this.renderTableWithTag(productItem, packetItem);
    }
    return this.renderNothingChosen();
  }
}

export default inject('cmsDataStore')(ProductOverview);
